import React, { useState } from 'react'
import { compose } from 'redux'
import CtaButton from 'components/cta-button'
import Font from '@cred/react-font'
import { Testable } from '@cred/react-testable'
import { onlyMobile, exceptMobile } from 'common/visibility'
import { product as spProduct, component as spContext } from '@cred/react-analytics/contexts'
import PropTypes from 'prop-types'
import { currency, number } from '@cred/shared/formatters'
import { withStyles } from '@cred/react-theme'
import { collapsibleClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import DisclosureModal from 'multi-vertical/rate-table/components/disclosure-modal'

import lenderDashboardImages from '../lender-logos'
import DisclaimerContent, { disclaimerContents } from './disclaimer-content'
import defaultStyles from './styles.module.scss'
import homes1 from './homes1.module.scss'
import multiVerticalStyles from './multiVerticalStyles.scss'

const getFontTypesFromMultivertical = isMultiVertical => ({
  medium: isMultiVertical ? 'disclaimer' : 'body',
  large: isMultiVertical ? 'bodyLarge' : 'large',
})

const lendersWithDisclaimerList = ['Stearns Lending']

const ProductTile = ({
  product: {
    discount,
    uuid,
    externalProductId,
    lender: { name, slug, nmls },
    monthlyPayment,
    apr,
    rate,
    fees,
    loanTerm,
    amortizationType,
  },
  onGetStarted,
  dashboardContexts,
  isMultiVertical,
  styles: defaultStylesProps,
  trackSnowplowEvent,
  multiVerticalVariation,
}) => {
  const [isOpen, setOpen] = useState(false)
  const styles = isMultiVertical ? multiVerticalStyles : defaultStylesProps
  const fontTypes = getFontTypesFromMultivertical(isMultiVertical)
  const iconClass = isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
  const isDisclaimer = lendersWithDisclaimerList.includes(name)
  const isShortenedMultivertical = multiVerticalVariation === 'shortened'

  const toggleLenderDisclaimer = () => {
    setOpen(!isOpen)
    trackSnowplowEvent(collapsibleClick, {
      text: 'Disclaimers',
      action: isOpen ? 'collapse' : 'expand',
    })
  }

  return (
    <>
      <Testable
        className={`${styles.container} ${isDisclaimer ? styles.flexWrap : ''}`}
        key={uuid}
        testId="product"
        id={externalProductId}
      >
        <div className={`${styles.firstRow} ${isDisclaimer ? styles.marginBottomZero : ''}`}>
          <div className={styles.lenderSection}>
            <img className={styles.itemLenderLogo} src={lenderDashboardImages(slug)} alt={name} />
            <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.nmlsText}`}>
              NMLS #{nmls}
            </Font>
            {isDisclaimer && (
              isShortenedMultivertical
                ? (
                  <DisclosureModal title="Disclaimers" name={`${name}-disclaimer`}>
                    <Font type="disclaimer">{disclaimerContents[name]}</Font>
                  </DisclosureModal>
                )
                : (
                  <Font color="lighter" onClick={toggleLenderDisclaimer} className={styles.disclaimersLink}>
                     Disclaimers <span className={`cred-icon ${iconClass}`} />
                  </Font>
                )
            )}
          </div>
          <div className={`${styles.buttonSectionMobile}`}>
            <CtaButton
              className={styles.ctaMobile}
              onClick={onGetStarted}
              analyticsContexts={[
                ...dashboardContexts,
                spProduct.analytics({
                  loanProductId: `${externalProductId}`,
                  eligibilityProductId: '0',
                }),
              ]}
            >
              Get Started
            </CtaButton>
            <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.ctaModifierMobile}`}>
              on Credible’s website
            </Font>
          </div>
        </div>
        <Testable className={styles.secondRow} testId="product-details">
          <Testable className={styles.itemSection} testId="monthly-payment-details">
            <Font type={fontTypes.medium} color="lighter" className={onlyMobile}>
              Mo. payment
            </Font>
            <Font type={fontTypes.medium} color="lighter" className={exceptMobile}>
              Monthly payment
            </Font>
            <Testable testId="amount">
              <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>
                {currency(monthlyPayment)}
              </Font>
            </Testable>
            <Testable testId="loan-type">
              {amortizationType === 'Fixed' && (
                <Font
                  type="disclaimer"
                  weight={isMultiVertical ? 'semiBold' : ''}
                  color={isMultiVertical ? 'secondary' : 'accent'}
                >
                  {loanTerm}-year fixed
                </Font>
              )}
              {amortizationType === 'ARM' && (
                <Font
                  type="disclaimer"
                  weight={isMultiVertical ? 'semiBold' : ''}
                  color={isMultiVertical ? 'secondary' : 'accent'}
                >
                  5/1 adjustable
                </Font>
              )}
            </Testable>
          </Testable>
          <Testable className={styles.itemSection} testId="apr-details">
            <Font type={fontTypes.medium} color="lighter">
              APR
            </Font>
            <Testable testId="amount">
              <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>
                {number(apr || 0, 3)}%
              </Font>
            </Testable>
            <Testable testId="rate-type">
              <Font
                type="disclaimer"
                weight={isMultiVertical ? 'semiBold' : ''}
                color={isMultiVertical ? 'secondary' : 'accent'}
              >
                {number(rate || 0, 3)}% Rate
              </Font>
            </Testable>
          </Testable>
          <Testable className={styles.itemSection} testId="fees-details">
            <Font type={fontTypes.medium} color="lighter">
              Fees
            </Font>
            <Testable testId="amount">
              <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>
                {currency((fees < 0 ? 0 : fees) || 0)}
              </Font>
            </Testable>
            <Testable testId="fees-discount">
              <Font
                type="disclaimer"
                weight={isMultiVertical ? 'semiBold' : ''}
                color={isMultiVertical ? 'secondary' : 'accent'}
              >
                {discount} Points
              </Font>
            </Testable>
          </Testable>
          <div className={styles.buttonSectionDesktop}>
            <CtaButton
              className={!isMultiVertical ? styles.ctaDesktop : ''}
              small={isMultiVertical}
              onClick={onGetStarted}
              analyticsContexts={[
                ...dashboardContexts,
                spProduct.analytics({
                  loanProductId: `${externalProductId}`,
                  eligibilityProductId: '0',
                }),
                spContext.analytics({ name: 'Product tile', description: `${name}, ${rate}` }),
              ]}
            >
              Get Started
            </CtaButton>
            <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.ctaModifierTablet}`}>
              on Credible’s website
            </Font>
          </div>
        </Testable>
        {isDisclaimer && (
          <DisclaimerContent
            className={`${styles.thirdRow} ${!isOpen ? styles.displayNone : ''}`}
            isOpen={isOpen}
            lenderName={name}
          />
        )}
      </Testable>
    </>
  )
}

ProductTile.propTypes = {
  product: PropTypes.object.isRequired,
  onGetStarted: PropTypes.func.isRequired,
  dashboardContexts: PropTypes.array.isRequired,
  isMultiVertical: PropTypes.bool,
  styles: PropTypes.object,
  trackSnowplowEvent: PropTypes.func,
  multiVerticalVariation: PropTypes.string,
}

export default compose(withSnowplowContexts, withStyles(defaultStyles, { homes1 }))(ProductTile)
